import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import {
  ItemModifierGroupUI,
  ItemModifierUI
} from '../pages/v2-order-pay/models/item-modifier-ui.model';
import { filter, map } from 'rxjs/operators';
import { V2BottomSheetRef } from '../v2-components/helpers/v2-bottom-sheet-ref';
import { ItemDetailsStore } from 'stores/item-details.store';
import { RxUtil } from 'utility/rx.util';

@Injectable({
  providedIn: 'root'
})
export class NestedModGroupSheetStore {
  private readonly _currentModifierGroupSheetParentModifierOwnershipChainKeyStack$ =
    new BehaviorSubject<string[]>([]);

  constructor(private itemDetailsStore: ItemDetailsStore) {}

  private readonly currentModifierGroupSheetParentModifierOwnershipChainKey$: Observable<string> =
    this._currentModifierGroupSheetParentModifierOwnershipChainKeyStack$.pipe(
      filter((stack) => stack.length > 0),
      map((stack) => stack[stack.length - 1])
    );

  readonly currentModifierGroupSheetParentModifier$: Observable<ItemModifierUI> =
    combineLatest([
      this.itemDetailsStore.modifierGroupsForSelectedItem$,
      this.currentModifierGroupSheetParentModifierOwnershipChainKey$
    ]).pipe(
      map(
        ([
          modifierGroupsForSelectedItem,
          currentModifierGroupSheetParentModifierOwnershipChainKey
        ]) =>
          this.findModifierInModGroupTree(
            currentModifierGroupSheetParentModifierOwnershipChainKey,
            modifierGroupsForSelectedItem
          )
      ),
      filter(RxUtil.inputIsNotNullOrUndefined)
    );

  private findModifierInModGroupTree(
    modifierOwnershipChainKey: string,
    modGroups: ItemModifierGroupUI[]
  ): ItemModifierUI | null {
    for (const mg of modGroups) {
      for (const m of mg.modifiers) {
        if (modifierOwnershipChainKey === m.ownershipChainKey) {
          return m;
        }

        const modFromSubTree = this.findModifierInModGroupTree(
          modifierOwnershipChainKey,
          m.modifierGroups
        );
        if (modFromSubTree) {
          return modFromSubTree;
        }
      }
    }
    return null;
  }

  set currentModifierGroupSheetParentModifier(v: ItemModifierUI) {
    const stack =
      this._currentModifierGroupSheetParentModifierOwnershipChainKeyStack$.getValue();
    stack.push(v.ownershipChainKey);
    this._currentModifierGroupSheetParentModifierOwnershipChainKeyStack$.next(
      stack
    );
  }

  popSheetStack() {
    const stack =
      this._currentModifierGroupSheetParentModifierOwnershipChainKeyStack$.getValue();
    stack.pop();
    this._currentModifierGroupSheetParentModifierOwnershipChainKeyStack$.next(
      stack
    );
  }

  clearCurrentModifierGroupSheetParentModifierStack() {
    this._currentModifierGroupSheetParentModifierOwnershipChainKeyStack$.next(
      []
    );
  }

  readonly isTopLevelOfModifierGroupSheetStack$: Observable<boolean> =
    this._currentModifierGroupSheetParentModifierOwnershipChainKeyStack$.pipe(
      map((stack) => stack.length === 1)
    );

  private readonly _currentNestedModSheet$ =
    new BehaviorSubject<V2BottomSheetRef | null>(null);

  get currentNestedModSheet(): V2BottomSheetRef | null {
    return this._currentNestedModSheet$.getValue();
  }

  set currentNestedModSheet(v: V2BottomSheetRef | null) {
    this._currentNestedModSheet$.next(v);
  }

  private readonly _scrollBottomSheetToElementById$ = new BehaviorSubject<
    string | null
  >(null);

  get currentScrollBottomSheetToElementById(): string | null {
    return this._scrollBottomSheetToElementById$.getValue();
  }

  set currentScrollBottomSheetToElementById(v: string | null) {
    this._scrollBottomSheetToElementById$.next(v);
  }

  clearCurrentScrollBottomSheetToElementById() {
    this._scrollBottomSheetToElementById$.next(null);
  }
}
